<template>
	<Default :loading="loading">
		<div class="w-full h-full relative md:flex">
			<div class="grow flex flex-col md:translate-x-0 transform transition-transform duration-300 ease-in-out translate-x-0 h-full">
				<!-- Container -->
				<div class="grow p-3 bg-gray-100">
					<!-- Page header -->
					<div class="md:flex md:items-center">
						<!-- Left: Title -->
						<h1 class="text-2xl md:text-3xl text-gray-800 font-bold mb-2 md:mb-0 flex-1">Roles de Usuarios</h1>
						<!-- More actions -->
						<div class="flex flex-col md:flex-row gap-y-2 md:gap-x-2" v-if="user.permissions?.includes('role.create')">
							<Button @click="$router.push({ name: 'go.newRole' })">
								<icon name="ri-add-circle-line" class="mr-1" />
								Añadir rol
							</Button>
						</div>
					</div>

					<!-- Table -->
					<div class="bg-white rounded-sm border border-gray-200 relative mt-3">
						<div class="md:flex md:items-center py-2 px-4">
							<header class="mb-2 md:mb-0 flex-1 font-semibold text-slate-800">
								Resultados:
								<span class="text-slate-400">{{ total }}</span>
							</header>
							<div class="gap-2 flex items-center">
								<div>
									Mostrar:
									<select v-model="options.limit" class="ml-2 form-select flex-1 md:flex-auto" @change="onChangeLimit">
										<option :value="5">5</option>
										<option :value="10">10</option>
										<option :value="20">20</option>
										<option :value="50">50</option>
										<option :value="100">100</option>
										<option :value="150">150</option>
									</select>
								</div>
							</div>
						</div>

						<!-- Table -->
						<div class="overflow-x-auto" v-if="!loading && items.length">
							<table class="table-auto w-full">
								<!-- Table header -->
								<thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-b border-t border-gray-200">
									<tr>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('label')">
													<p class="uppercase mr-1">Rol</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'label'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('description')">
													<p class="uppercase mr-1">Descripción</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'description'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">Max Inactividad</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-center">Nº Usuarios</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">
											<div class="font-semibold text-left">Acciones</div>
										</th>
									</tr>
								</thead>
								<!-- Table body -->
								<tbody class="text-sm divide-y divide-gray-200">
									<tr class="even:bg-gray-100 hover:bg-gray-200" v-for="item in items" :key="item._id">
										<td class="px-2 first:pl-5 last:pr-5 py-2 whitespace-nowrap">{{ item.label }}</td>
										<td class="px-2">
											{{ item.description }}
										</td>
										<td class="px-2">{{ dayjs.duration((item.config.maxInactivityTime || 0) * 1000).humanize() }}</td>
										<td class="px-2" align="center">
											{{ item.usersCount }}
										</td>
										<td class="px-2 first:pl-5 last:pr-5 py-1 flex flex-wrap gap-2">
											<Button
												@click="
													$router.push({
														name: 'go.role',
														params: { _id: item._id }
													})
												"
												color="warning"
												:disabled="!user.permissions?.includes('role.update')"
											>
												<icon name="ri-pencil-line" />
											</Button>
											<Button :loading="loading" color="danger" @click="deleteRole(item._id)" :disabled="loading || !!item.usersCount || !user.permissions?.includes('role.delete')">
												<icon name="ri-delete-bin-7-line" />
											</Button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<!-- Pagination -->
					<div class="mt-8 mb-24" v-if="!loading && items.length">
						<Pagination v-show="total > options.limit" :results="items.length" :skip="options.skip" :limit="options.limit" :total="total" @page="updatePage" />
					</div>

					<!-- No results -->
					<div class="bg-white p-4 rounded-sm border border-gray-200 mt-3" v-if="(!loading && !items.length) || errors.length">
						<Banner type="error" :open="!!errors?.length" fixed>
							<ul>
								<li v-for="(error, i) in errors" :key="i">{{ error.message }}</li>
							</ul>
						</Banner>

						<div class="w-full flex justify-center p-4" v-if="!loading && !items.length">
							<div class="text-center py-6">
								<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-300 to-red-100 mb-4">
									<icon name="ri-information-line" scale="2" />
								</div>
								<h2 class="text-2xl text-gray-800 font-bold mb-2">No se han encontrado usuarios.</h2>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Default>
</template>

<script setup lang="ts">
import { ValidationError } from '@/errors/ValidationError'
import { dayjs } from '@/services/dayjs'
import { user } from '@/user'
import { Ref, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Banner from '../../components/Banner.vue'
import Button from '../../components/Button.vue'
import Pagination from '../../components/Pagination.vue'
import { del, get } from '../../services/api'
import Default from '../Default.vue'

const route = useRoute()
const router = useRouter()

const options: Record<string, any> = reactive({
	...route.query,
	limit: route.query.limit ? parseInt(<string>route.query.limit) : 20,
	skip: route.query.skip ? parseInt(<string>route.query.skip) : 0,
	sort: route.query.sort || 'label'
})

const loading = ref(false)
const items = ref([])
const total = ref(0)
const errors = ref([])
const sortBy = reactive({ field: (<string>options.sort || '')?.split('-')[0], type: options.sort?.includes('-') ? '-' : '' })

const handleSort = column => {
	if (column != sortBy.field) {
		sortBy.field = column
		sortBy.type = ''
	} else {
		sortBy.type = sortBy.type === '-' ? '' : '-'
	}
	options.sort = `${sortBy.type}${sortBy.field}`
}

const search = async () => {
	loading.value = true
	errors.value = []
	try {
		await router.replace({
			query: options
		})
		items.value = []
		const query = { ...route.query, projection: 'role,description,label,config.maxInactivityTime' }
		const { data, pagination } = await get({ name: 'role', query: query })
		total.value = pagination.total
		items.value = user.value?.role !== 'root' ? data.filter(role => role.role !== 'root') : data
	} catch (error) {
		if (error instanceof ValidationError) {
			errors.value = error.errors || []
		}
	}
	loading.value = false
}

onMounted(() => {
	search()
})

const onChangeLimit = () => {
	options.skip = 0
}

const updatePage = (page: Ref) => {
	options.skip = page.value == -1 ? 0 : (page.value - 1) * options.limit
}
watch(options, () => {
	search()
})

const deleteRole = async (_id: string) => {
	if (window.confirm('¿Estás seguro que deseas eliminar el rol?')) {
		loading.value = true
		try {
			await del({ name: 'role', params: { _id } })
			search()
		} catch (error) {
			alert('Error al eliminar el rol')
		}
		loading.value = false
	}
}
</script>
